import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, toValue } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { t } from '@/i18n/index.js'
import { useUi } from '@/stores/ui.js'

/**
 * Unsaved work on a page. Every editor of a page registers what it holds, and one bar under the
 * page — the same on a phone and a desktop — says so and saves it, wherever the change was
 * made. Dialogs and side panels keep their own pinned footers and do not register.
 *
 * Options are values, refs or getters:
 *   dirty   — something would be lost;
 *   save    — saves it; returns null or false when it did not (invalid, refused);
 *   discard — goes back to what was saved (optional);
 *   count   — how many fields changed (optional);
 *   saving  — a save is running;
 *   create  — a new record: the bar is there from the start and says "Create";
 *   guard   — ask before leaving the page or closing the tab (false where the page asks itself);
 *   marks   — {on(), toggle()}: the page can show what waits in its own colour (D-170) — the bar
 *             offers the switch.
 */
const entries = reactive([])
let next = 0

/** The bar shows the editor registered last that has something to save. */
export const unsavedEntry = computed(() => [...entries].reverse().find((e) => e.shown()) || null)

export function useUnsaved({ dirty, save, discard = null, count = null, saving = false, create = false, guard = true, marks = null }) {
  const entry = {
    id: ++next,
    dirty: () => Boolean(toValue(dirty)),
    shown: () => Boolean(toValue(create)) || Boolean(toValue(dirty)),
    create: () => Boolean(toValue(create)),
    count: () => toValue(count),
    saving: () => Boolean(toValue(saving)),
    save,
    discard,
    marks,
  }
  onMounted(() => entries.push(entry))
  onBeforeUnmount(() => {
    const i = entries.indexOf(entry)
    if (i >= 0) entries.splice(i, 1)
  })

  if (guard) {
    const ui = useUi()
    onBeforeRouteLeave(async () => (entry.dirty() ? await ui.ask({ title: t('common.unsaved'), message: t('common.leaveUnsaved'), confirmLabel: t('record.leaveDiscard'), danger: true }) : true))
    const beforeUnload = (e) => {
      if (!entry.dirty()) return
      e.preventDefault()
      e.returnValue = ''
    }
    onMounted(() => window.addEventListener('beforeunload', beforeUnload))
    onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))
  }
  return entry
}

/** A form's state compared with a snapshot taken when it was loaded or saved. */
export function useSnapshot(source) {
  const state = reactive({ saved: JSON.stringify(toValue(source)) })
  return {
    dirty: computed(() => JSON.stringify(toValue(source)) !== state.saved),
    mark: () => (state.saved = JSON.stringify(toValue(source))),
    restore: () => JSON.parse(state.saved),
  }
}

/**
 * A page that edits one thing (a catalogue element): what it holds compared with what was loaded
 * or saved, on the bar under the page. `settle()` after a load takes the state as saved once the
 * page's editors have shaped it (filled their defaults); `save` returns false when it did not
 * save; `restore` puts back what was saved. Till settled — while loading (`loading()` when it loads
 * anew) — nothing is changed: a form filled before the rest of the page arrives is not an edit.
 */
export function usePageEdit(source, { save, restore = null, create = false }) {
  const snapshot = useSnapshot(source)
  const saving = ref(false)
  const settled = ref(false)
  const dirty = computed(() => settled.value && snapshot.dirty.value)
  const loading = () => (settled.value = false)
  const settle = async () => {
    snapshot.mark()
    await nextTick()
    await nextTick()
    snapshot.mark()
    settled.value = true
    // Loaded or saved anew: a page showing it among others (a product's map) looks again.
    window.dispatchEvent(new CustomEvent('catalogue:changed'))
  }
  useUnsaved({
    dirty,
    saving,
    create,
    save: async () => {
      saving.value = true
      try {
        return await save()
      } finally {
        saving.value = false
      }
    },
    discard: restore ? () => restore(snapshot.restore()) : null,
  })
  return { settle, loading, dirty }
}
